import { AuthenticatedRoute } from '@/app/constant';

/**
 * KEYBOARD_BINDINGS — the single source of truth for the app's keyboard
 * shortcuts AND the `?` help overlay's cheat-sheet content. Both the handlers
 * (e.g. `useGoToNavigation`) and the overlay (`KeyboardHelpOverlay`) iterate
 * these same arrays, so the bindings and their documentation cannot drift.
 *
 * `keys`    is the react-hotkeys-hook keys string. A `>` makes it a sequence
 *           (the library auto-derives `isSequence` from the presence of the
 *           default `sequenceSplitKey` `'>'`); `'shift+slash'` is a combo.
 *           Matching is on `event.code` (so `/` is `slash`, `?` would not
 *           match — it matches the produced char, not the code).
 * `display` is the human-readable key hint rendered as `<kbd>` chips.
 * `label`   is the action description shown in the overlay.
 * `route`   (navigation only) is the `AuthenticatedRoute` the sequence opens.
 *
 * The POE `p` toggle and the Technologies `c`/`t`/`e` actions are registered at
 * their own call sites (drawer/page-local concerns); they appear here so the
 * overlay can document them and stay accurate. The same goes for the shared
 * table row-action shortcuts (`vulnerabilities`, `assets`, `seeds`): those are
 * bound by the `Table`/`CanonicalTable` row-action system from each section's
 * `keyboardActions` config (bare letters, `altKey: false`) — they appear here
 * for documentation only so the overlay lists them.
 */
export const KEYBOARD_BINDINGS = {
  navigation: [
    {
      keys: 'g>i',
      display: 'g i',
      label: 'Insights',
      route: AuthenticatedRoute.INSIGHTS,
    },
    {
      keys: 'g>s',
      display: 'g s',
      label: 'Seeds',
      route: AuthenticatedRoute.SEEDS,
    },
    {
      keys: 'g>a',
      display: 'g a',
      label: 'Assets',
      route: AuthenticatedRoute.ASSETS,
    },
    {
      keys: 'g>t',
      display: 'g t',
      label: 'Technologies',
      route: AuthenticatedRoute.TECHNOLOGIES,
    },
    {
      keys: 'g>v',
      display: 'g v',
      label: 'Vulnerabilities',
      route: AuthenticatedRoute.VULNERABILITIES,
    },
    {
      keys: 'g>h',
      display: 'g h',
      label: 'Threats',
      route: AuthenticatedRoute.THREATS,
    },
    {
      keys: 'g>k',
      display: 'g k',
      label: 'Attacks',
      route: AuthenticatedRoute.ATTACKS,
    },
    {
      keys: 'g>l',
      display: 'g l',
      label: 'Detections',
      route: AuthenticatedRoute.VALIDATIONS,
    },
    {
      keys: 'g>n',
      display: 'g n',
      label: 'Integrations',
      route: AuthenticatedRoute.INTEGRATIONS,
    },
    {
      keys: 'g>e',
      display: 'g e',
      label: 'Endpoints',
      route: AuthenticatedRoute.AGENTS,
    },
    {
      keys: 'g>j',
      display: 'g j',
      label: 'Jobs',
      route: AuthenticatedRoute.JOBS,
    },
    {
      keys: 'g>d',
      display: 'g d',
      label: 'Documents',
      route: AuthenticatedRoute.FILES,
    },
  ],
  poe: [{ keys: 'p', display: 'p', label: 'Toggle Proof-of-Exploit mode' }],
  technology: [
    { keys: 'c', display: 'c', label: 'Add note (selected)' },
    { keys: 't', display: 't', label: 'Add tag (selected)' },
    { keys: 'e', display: 'e', label: 'Export (selected)' },
  ],
  vulnerabilities: [
    { keys: 't', display: 't', label: 'Mark as Triaged (Praetorian)' },
    { keys: 'o', display: 'o', label: 'Mark as Opened' },
    { keys: 'c', display: 'c', label: 'Mark as Remediated' },
    { keys: 'a', display: 'a', label: 'Mark as Accepted' },
    { keys: 'r', display: 'r', label: 'Reject / Delete' },
  ],
  assets: [
    { keys: 'f', display: 'f', label: 'Freeze' },
    { keys: 'u', display: 'u', label: 'Unfreeze' },
    { keys: 'd', display: 'd', label: 'Delete' },
  ],
  seeds: [
    { keys: 'a', display: 'a', label: 'Accept' },
    { keys: 'r', display: 'r', label: 'Reject' },
    { keys: 'd', display: 'd', label: 'Delete' },
    { keys: 'f', display: 'f', label: 'Freeze' },
  ],
  // Documentation-only (same convention as the row-action groups above): the
  // handler is NOT registered here — push-to-talk is a modifier-HOLD with a
  // paired keyup, which react-hotkeys-hook v5 cannot model, so it binds raw
  // window listeners inside `useVoiceInput` (sections/insights/aiAssistant/
  // hooks/), gated on the Marcus surface being mounted + the `global` scope.
  // The physical key is right Ctrl; on provisioned Macs the fn key is remapped
  // to it (hidutil), delivering the Wispr-Flow-style fn-hold UX.
  aiAssistant: [
    {
      keys: 'hold ControlRight',
      display: 'hold right ⌃',
      label: 'Push-to-talk voice input (Marcus chat)',
    },
  ],
  help: [
    { keys: 'shift+slash', display: '?', label: 'Show keyboard shortcuts' },
  ],
} as const;

/** A single navigation binding (`g>x` sequence → route). */
export type NavigationBinding = (typeof KEYBOARD_BINDINGS.navigation)[number];
